/* useFloodRisk — polls /api/flood-risk for the active location.
 *
 * Cache + 30-min revalidate matches the backend TTL exactly.
 * Returns { data, status, error } where status is 'idle'|'loading'|'ready'|'error'.
 */
import { useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function useFloodRisk(location) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const lastKey = useRef(null);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;
    const key = `${location.latitude.toFixed(3)}|${location.longitude.toFixed(3)}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    const url = `${BACKEND}/api/flood-risk?lat=${location.latitude}&lon=${location.longitude}`;
    fetch(url, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        setData(json);
        setStatus("ready");
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(e.message || "Failed to load flood data");
        setStatus("error");
      });

    return () => controller.abort();
  }, [location?.latitude, location?.longitude]);

  return { data, status, error };
}
